<template>
  <v-container fluid class="storefront-page pa-0">
    <div class="storefront-shell">
      <div class="storefront-topbar">
        <div>
          <div class="storefront-title">
            {{ businessName }}
          </div>
          <div class="storefront-subtitle">
            Shop specials, new products, and start your order
          </div>
        </div>

        <v-btn
          rounded
          :color="backgroundColor"
          :dark="darkMode"
          @click="startOrder"
        >
          Start Order
        </v-btn>
      </div>

      <div class="hero-section">
        <v-carousel
          v-if="banners.length"
          height="210"
          hide-delimiter-background
          show-arrows-on-hover
          cycle
        >
          <v-carousel-item v-for="(banner, index) in banners" :key="index">
            <v-img
              :src="banner.imageUrl"
              height="210"
              class="hero-image"
              gradient="to bottom, rgba(15,23,42,.15), rgba(15,23,42,.55)"
            >
              <div class="hero-content">
                <div class="hero-title">
                  {{ banner.title }}
                </div>
                <div class="hero-subtitle">
                  {{ banner.subtitle }}
                </div>
              </div>
            </v-img>
          </v-carousel-item>
        </v-carousel>

        <div v-else class="hero-placeholder">
          <div>
            <div class="hero-title dark">Featured Wholesale Deals</div>
            <div class="hero-subtitle dark">
              Browse specials, new products, and popular items.
            </div>
          </div>

          <v-btn
            rounded
            :color="backgroundColor"
            :dark="darkMode"
            @click="startOrder"
          >
            Start Order
          </v-btn>
        </div>
      </div>

      <template v-if="specialProducts.length">
        <div class="section-header">
          <div>
            <div class="section-title">Special Prices</div>
            <div class="section-subtitle">Best available pricing</div>
          </div>

          <v-btn
            text
            :color="backgroundColor"
            :dark="darkMode"
            @click="startOrderWithTab('special_prices')"
          >
            View All
          </v-btn>
        </div>

        <ProductRow
          :loading="loadingSpecials"
          :products="specialProducts"
          @open="openProduct"
        />
      </template>

      <template v-if="newProducts.length">
        <div class="section-header">
          <div>
            <div class="section-title">New Products</div>
            <div class="section-subtitle">Recently added items</div>
          </div>

          <v-btn
            text
            :color="backgroundColor"
            :dark="darkMode"
            @click="startOrderWithTab('new_product')"
          >
            View All
          </v-btn>
        </div>

        <ProductRow
          :loading="loadingNew"
          :products="newProducts"
          @open="openProduct"
        />
      </template>

      <div
        v-if="
          !loadingSpecials &&
          !loadingNew &&
          !specialProducts.length &&
          !newProducts.length
        "
        class="empty-home-section"
      >
        <v-icon size="46" color="grey lighten-1">mdi-storefront-outline</v-icon>

        <div class="empty-home-title">Catalog is ready</div>

        <div class="empty-home-subtitle">
          Start an order to browse available products.
        </div>

        <v-btn
          rounded
          class="mt-3"
          :color="backgroundColor"
          :dark="darkMode"
          @click="startOrder"
        >
          Start Order
        </v-btn>
      </div>

      <div class="bottom-start-bar">
        <div>
          <div class="bottom-title">Ready to place an order?</div>
          <div class="bottom-subtitle">Start full catalog ordering</div>
        </div>

        <v-btn
          rounded
          :color="backgroundColor"
          :dark="darkMode"
          @click="startOrder"
        >
          Start Order
        </v-btn>
      </div>
    </div>

    <v-dialog v-model="productDialog" max-width="520px">
      <v-card v-if="selectedProduct" class="rounded-xl">
        <v-img
          v-if="selectedProduct.imageUrl"
          :src="selectedProduct.imageUrl"
          height="260"
          contain
          class="product-preview-image"
        />

        <v-card-text class="pa-4">
          <div class="preview-title">
            {{ selectedProduct.name }}
          </div>

          <div class="preview-meta">
            <v-chip
              v-if="isSpecialProduct(selectedProduct)"
              small
              color="red lighten-5"
              text-color="red darken-3"
              class="font-weight-bold mr-2"
            >
              Special Price
            </v-chip>

            <v-chip
              v-if="isNewProduct(selectedProduct)"
              small
              color="green lighten-5"
              text-color="green darken-3"
              class="font-weight-bold"
            >
              New Product
            </v-chip>
          </div>

          <div class="preview-price">
            {{ selectedProduct.price | currency }}
          </div>
        </v-card-text>

        <v-card-actions>
          <v-btn text @click="productDialog = false">Close</v-btn>
          <v-spacer />
          <v-btn color="primary" rounded dark @click="startOrder">
            Start Order
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <PublicCustomerStartDialog
      v-model="customerDialog"
      @continue="startPublicOrder"
      :buttonColor="backgroundColor"
      :darkMode="darkMode"
      :loading="startingOrder"
      :businessCode="code"
    />
  </v-container>
</template>

<script>
import ProductRow from "@/modules/Order/components/PublicCatalog/ProductRow.vue";
import orderService from "@/modules/Order/components/PublicCatalog/service.js";
import PublicCustomerStartDialog from "@/modules/Order/components/PublicCatalog/PublicCustomerStartDialog.vue";
import Hashids from "hashids";

const hashids = new Hashids();

export default {
  name: "public-storefront-home",

  components: {
    ProductRow,
    PublicCustomerStartDialog,
  },

  data() {
    return {
      code: null,
      business: null,
      search: "",
      customerDialog: false,
      startingOrder: false,
      loadingPage: true,
      loadingSpecials: false,
      loadingNew: false,
      specialProducts: [],
      newProducts: [],
      productDialog: false,
      selectedProduct: null,
    };
  },
  computed: {
    collectionId() {
      return this.$route.query.collectionId || null;
    },
    businessName() {
      if (
        this.business &&
        this.business.metadata &&
        this.business.metadata.remoteOrderBusinessName
      ) {
        return this.business.name;
      }

      return "Wholesale Catalog";
    },

    backgroundColor() {
      if (
        this.business &&
        this.business.metadata &&
        this.business.metadata.remoteOrderBackgroundColor
      ) {
        return this.business.metadata.remoteOrderBackgroundColor;
      }

      return "#F0C316";
    },

    darkMode() {
      return !!(
        this.business &&
        this.business.metadata &&
        this.business.metadata.remoteOrderDarkMode
      );
    },

    banners() {
      if (
        this.business &&
        this.business.metadata &&
        Array.isArray(this.business.metadata.remoteOrderBanners) &&
        this.business.metadata.remoteOrderBanners.length
      ) {
        return this.business.metadata.remoteOrderBanners;
      }

      if (this.business && this.business.imageUrl) {
        return [
          {
            title: this.business.name,
            subtitle: "Browse specials, new products, and popular items.",
            imageUrl: this.business.imageUrl,
          },
        ];
      }

      return [];
    },
  },

  async created() {
    await this.loadPublicBusiness();
    await this.loadHomeProducts();
  },

  methods: {
    getPublicDeviceKey() {
      const key = `${process.env.VUE_APP_STORAGE_PREFIX}publicDeviceKey_${this.code}`;
      let deviceKey = localStorage.getItem(key);

      if (!deviceKey) {
        deviceKey = `pub_${Date.now()}_${Math.random()
          .toString(36)
          .substring(2, 10)}`;

        localStorage.setItem(key, deviceKey);
      }

      return deviceKey;
    },
    async startPublicOrder({ customerInfo, verificationToken }) {
      this.startingOrder = true;

      try {
        localStorage.setItem(
          `${process.env.VUE_APP_STORAGE_PREFIX}publicOrderCustomer_${this.code}`,
          JSON.stringify(customerInfo)
        );

        const deviceKey = this.getPublicDeviceKey();

        const payload = {
          id: null,
          date: this.$moment().toISOString(),
          status: "draft",
          source: "remote",
          isRemoteOrder: true,
          metadata: {
            publicCustomer: customerInfo,
            deviceKey: this.getPublicDeviceKey(),
          },
          verificationToken,
          OrderDetails: [],
        };

        const response = await orderService.publicAutosaveDraft(
          this.code,
          payload
        );

        const order = response.data;

        console.log("Started public order", order);

        localStorage.setItem(
          `${process.env.VUE_APP_STORAGE_PREFIX}publicOrderDraft_${this.code}`,
          JSON.stringify({
            orderId: order.id,
            deviceKey,
            customerInfo,
          })
        );

        this.customerDialog = false;

        this.$router.push({
          path: `/p/o/${this.$route.params.id}/catalog/${order.id}`,
          query: {
            collectionId: this.collectionId,
          },
        });
      } catch (error) {
        console.error("start public order failed", error);
        this.$swal("Start Order", "Unable to start order", "error");
      } finally {
        this.startingOrder = false;
      }
    },
    async loadPublicBusiness() {
      this.loadingPage = true;

      try {
        const routeId = this.$route.params.id;

        if (!routeId) {
          throw new Error("Public route id is missing");
        }

        const hasProductId = routeId.includes("-");

        if (hasProductId) {
          const parts = routeId.split("-");
          this.code = hashids.decode(parts[0]).toString();
        } else {
          this.code = hashids.decode(routeId).toString();
        }

        const response = await orderService.getBusinessInfo(this.code);
        this.business = response.data.business;
      } catch (error) {
        console.error("load public business failed", error);
        this.business = null;
      } finally {
        this.loadingPage = false;
      }
    },

    async loadHomeProducts() {
      if (!this.code) return;

      await Promise.all([this.getSpecialProducts(), this.getNewProducts()]);
    },

    getProductBaseParams() {
      const where = {
        key: this.code,
        hideParent: true,
        remoteOrder: true,
        limit: 20,
        offset: 0,
        sortBy: ["Department.name", "Brand.name", "name"],
        sortDesc: [false, false, false],
      };

      if (
        this.business &&
        this.business.metadata &&
        this.business.metadata.showOutOfStockItems
      ) {
        where.hideZero = false;
      } else {
        where.hideZero = true;
      }

      return where;
    },

    async getSpecialProducts() {
      this.loadingSpecials = true;

      try {
        const response = await orderService.getPublicProduct({
          ...this.getProductBaseParams(),
          isSpecial: true,
        });

        this.specialProducts = response.data || [];
      } catch (error) {
        console.error("get special products failed", error);
        this.specialProducts = [];
      } finally {
        this.loadingSpecials = false;
      }
    },

    async getNewProducts() {
      this.loadingNew = true;

      try {
        const response = await orderService.getPublicProduct({
          ...this.getProductBaseParams(),
          isNew: true,
        });

        this.newProducts = response.data || [];
      } catch (error) {
        console.error("get new products failed", error);
        this.newProducts = [];
      } finally {
        this.loadingNew = false;
      }
    },

    openProduct(product) {
      this.selectedProduct = product;
      this.productDialog = true;
    },

    startOrder() {
      this.customerDialog = true;
    },

    startOrderWithTab(tab) {
      this.$router.push({
        path: `/p/o/${this.$route.params.id}`,
        query: {
          tab,
          collectionId: this.collectionId,
        },
      });
    },

    startOrderWithSearch() {
      this.$router.push({
        path: `/p/o/${this.$route.params.id}`,
        query: {
          search: this.search,
          collectionId: this.collectionId,
        },
      });
    },

    isSpecialProduct(product) {
      return !!(
        product &&
        (product.special ||
          product.isSpecial ||
          product.isSpecialPrice ||
          product.specialPrice)
      );
    },

    isNewProduct(product) {
      if (!product) return false;
      if (product.isNew || product.newProduct) return true;
      if (!product.createdAt) return false;

      const created = new Date(product.createdAt);
      const now = new Date();
      const diffDays = (now - created) / (1000 * 60 * 60 * 24);

      return diffDays <= 30;
    },
  },
};
</script>

<style scoped>
.storefront-page {
  background: #e5e7eb;
  min-height: 100vh;
}

.storefront-shell {
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 0 16px 96px;
}

.storefront-topbar {
  min-height: 64px;
  background: #ffffff;
  border-bottom: 1px solid #d1d5db;
  padding: 12px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  position: sticky;
  top: 0;
  z-index: 20;
  margin: 0 -16px;
}

.storefront-title {
  font-size: 22px;
  font-weight: 900;
  color: #0f172a;
  line-height: 1.1;
}

.storefront-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 3px;
}

.hero-section {
  padding: 14px 0;
}

.hero-image {
  border-radius: 18px;
  overflow: hidden;
}

.hero-content {
  height: 100%;
  padding: 26px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  color: #ffffff;
}

.hero-title {
  font-size: 28px;
  font-weight: 900;
}

.hero-subtitle {
  font-size: 15px;
  margin-top: 4px;
}

.hero-title.dark,
.hero-subtitle.dark {
  color: #0f172a;
}

.hero-placeholder {
  min-height: 210px;
  border-radius: 18px;
  background: linear-gradient(135deg, #e0f2fe, #fef3c7);
  border: 1px solid #e2e8f0;
  padding: 24px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
}

.search-wrap {
  padding: 0 0 12px;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
}

.search-btn {
  height: 40px !important;
  min-width: 110px;
}

.section-header {
  margin: 0;
  padding: 16px 18px 10px;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.section-title {
  font-size: 23px;
  font-weight: 900;
  color: #111827;
}

.section-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.bottom-start-bar {
  position: fixed;
  left: 18px;
  right: 18px;
  bottom: 14px;
  z-index: 30;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  box-shadow: 0 12px 34px rgba(15, 23, 42, 0.18);
  padding: 13px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.bottom-title {
  font-size: 15px;
  font-weight: 900;
  color: #0f172a;
}

.bottom-subtitle {
  font-size: 12px;
  color: #64748b;
}

.product-preview-image {
  background: #f8fafc;
}

.preview-title {
  font-size: 18px;
  font-weight: 900;
  color: #0f172a;
}

.preview-meta {
  margin-top: 8px;
}

.preview-price {
  margin-top: 12px;
  font-size: 24px;
  font-weight: 900;
  color: #0f172a;
}

.empty-home-section {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 34px 16px;
  text-align: center;
  margin-top: 16px;
}

.empty-home-title {
  font-size: 18px;
  font-weight: 900;
  color: #0f172a;
  margin-top: 10px;
}

.empty-home-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 4px;
}

@media (max-width: 600px) {
  .storefront-shell {
    width: 100%;
    max-width: 100%;
  }

  .storefront-topbar {
    margin: 0 -8px;
    min-height: 58px;
    padding: 10px 12px;
  }

  .storefront-title {
    font-size: 18px;
  }

  .storefront-subtitle {
    font-size: 11px;
  }

  .hero-section {
    padding: 8px 0;
  }

  .hero-placeholder {
    min-height: 180px;
    align-items: flex-start;
    flex-direction: column;
  }

  .hero-title {
    font-size: 22px;
  }

  .search-wrap {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .section-header {
    padding: 14px 10px 8px;
  }

  .section-title {
    font-size: 20px;
  }

  .bottom-start-bar {
    left: 8px;
    right: 8px;
    bottom: 8px;
    padding: 12px;
  }
}
</style>
